import { useState, useContext, useMemo, useEffect } from "react";
import { Button } from "@/modern/components/ui/button";
import { Input } from "@/modern/components/ui/input";
import { Card } from "@/modern/components/ui/card";
import { Controller } from "react-hook-form";
import { toast } from "sonner";
import { createSpotIncentive as createSpotIncentiveAPI } from "@/network/network.jsx";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { BaseModal } from "../../base/BaseModal.jsx";
import { BaseModalBody } from "../../base/BaseModalBody.jsx";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { useSwivuForm } from "@/react/hooks/useSwivuForm.jsx";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { getIsArchivedForBoard } from "@/other/utils/otherUtils";
import { REACT_EDITOR_CONTEXT_MENU_KEYS, SWIVU_ASSIST_CONFIG } from "@/other/constants/OtherKeys.jsx";
import { INCENTIVE_PERMISSION_KEYS } from "@/other/constants/IncentiveKeys.jsx";
import { checkIncentivePermission } from "@/other/utils/incentiveUtils.jsx";
import SpotIncentiveTaskSelector from "./SpotIncentiveTaskSelector";
import SpotIncentiveResourceSelector from "./SpotIncentiveResourceSelector";
import SpotIncentivePointsAssignment from "./SpotIncentivePointsAssignment";
import SpotIncentiveDetailModal from "./SpotIncentiveDetailModal";
import { spotIncentiveSchema, SpotIncentiveFormData } from "./spotIncentiveSchema";
import { Target, Users, FileText, Pencil, Trash2, Award } from "lucide-react";
import SpotIncentiveImagePreview, { ImageThumbnail } from "./SpotIncentiveImagePreview";
import { SpotIncentiveFileData } from "@/react/components/entityViews/files/utils/spotIncentiveFileUtils";

// ===== TYPE DEFINITIONS =====
export interface DetailCard {
  id: string;
  image?: File;
  imageUrl?: string;
  comment: string;
  timestamp: string;
  document_id?: number;
}

interface SpotIncentiveProps {
  record: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  onClose: () => void;
  editorInstance: unknown;
  selectedTask?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  initialFileData?: SpotIncentiveFileData;
}

// ===== COMPONENT =====
const SpotIncentive = ({ record, onClose, editorInstance, selectedTask, initialFileData }: SpotIncentiveProps) => {
  const { project } = useContext(BryntumContext) as any; // eslint-disable-line @typescript-eslint/no-explicit-any

  // Permission checking for context menu options
  const memoizedPermissionMap = useMemo(() => {
    const permissionKeys = [
      INCENTIVE_PERMISSION_KEYS.POINT_ARCHIVERS,
      INCENTIVE_PERMISSION_KEYS.POINT_DELETERS
    ];
    return permissionKeys.reduce((acc: Record<string, boolean>, permissionKey) => {
      acc[permissionKey] = checkIncentivePermission({ project, permissionKey });
      return acc;
    }, {});
  }, [project]);

  const { form, formState } = useSwivuForm({
    schema: spotIncentiveSchema,
    record: record, // Empty record for new spot incentive
    updateRecordOnFormValueChange: false,
    version: "",
    defaultValues: {
      name: "",
      taskId: selectedTask?.id || null,
      resourceIds: [],
      startDate: (() => {
        const date = new Date();
        date.setDate(date.getDate() - 5);
        return date.toISOString();
      })(),
      endDate: new Date().toISOString(),
      details: [],
      positiveTagIds: [],
      negativeTagIds: []
    }
  });

  const [showTaskSelector, setShowTaskSelector] = useState(false);
  const [showResourceSelector, setShowResourceSelector] = useState(false);
  const [showPointsAssignment, setShowPointsAssignment] = useState(false);
  const [editingDetail, setEditingDetail] = useState<DetailCard | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (selectedTask?.id) {
      form.setValue("taskId", selectedTask.id, { shouldValidate: false, shouldDirty: false });
    }

    // Set the file detail if provided
    if (initialFileData) {
      const newDetail: DetailCard = {
        id: Date.now().toString(),
        image: initialFileData.image,
        imageUrl: initialFileData.imageUrl,
        comment: initialFileData.comment,
        timestamp: new Date().toLocaleTimeString(),
        document_id: initialFileData.document_id,
      };
      form.setValue("details", [newDetail], { shouldValidate: false, shouldDirty: true });
    }
  }, []);

  const tasks = useMemo(() => project?.extraTaskStore?.query((record: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    const selectedBoard = getSavedBoardSelection();
    const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);
    return !record.isPhantom && (canShowArchivedRecords || !record.archived);
  }, true) || [], [project]);

  const getSelectedTaskName = (taskId: number | null) => {
    if (!taskId) return "Choose a task to assign incentives...";
    const task = tasks.find((t: any) => t.id === taskId); // eslint-disable-line @typescript-eslint/no-explicit-any
    return task?.name || "Task not found";
  };

  const getSelectedResourceNames = (resourceIds: number[]) => {
    if (resourceIds.length === 0) return ["No resources selected"];
    if (!project?.resourceStore) return resourceIds.map(id => `Resource ${id}`);

    return resourceIds.map(id => {
      const resource = project.resourceStore.getById(id);
      return resource?.name || `Resource ${id}`;
    });
  };

  const getDateRangeDisplay = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return "";
    const start = new Date(startDate).toLocaleDateString();
    const end = new Date(endDate).toLocaleDateString();
    return ` • ${start} - ${end}`;
  };

  const getTotalPoints = (): number => {
    let total = 0;

    // Calculate positive points
    form.watch("positiveTagIds").forEach((tagId: number) => {
      const tag = project?.incentiveTagsStore?.getById(tagId);
      if (tag && tag.categoryRecord) {
        total += tag.categoryRecord.points;
      }
    });

    // Calculate negative points
    form.watch("negativeTagIds").forEach((tagId: number) => {
      const tag = project?.incentiveTagsStore?.getById(tagId);
      if (tag && tag.categoryRecord) {
        total -= tag.categoryRecord.points;
      }
    });

    return total;
  };

  const handleAddDetail = (detail: { image?: File; comment: string; document_id?: number; id?: string }) => {
    const newDetail: DetailCard = {
      id: detail.id || Date.now().toString(),
      image: detail.image,
      comment: detail.comment,
      timestamp: new Date().toLocaleTimeString(),
      document_id: detail.document_id,
    };
    const currentDetails = form.watch("details");
    form.setValue("details", [...currentDetails, newDetail]);
  };

  const handleEditDetail = (id: string, detail: { image?: File; comment: string; document_id?: number }) => {
    const currentDetails = form.watch("details");
    form.setValue("details",
      currentDetails.map((d: any) => // eslint-disable-line @typescript-eslint/no-explicit-any
        d.id === id
          ? { ...d, image: detail.image, comment: detail.comment, document_id: detail.document_id }
          : d
      )
    );
    setEditingDetail(null);
  };

  const removeDetail = (id: string) => {
    const currentDetails = form.watch("details");
    form.setValue("details", currentDetails.filter((d: any) => d.id !== id)); // eslint-disable-line @typescript-eslint/no-explicit-any
  };

  const transformDataForAPI = (formData: SpotIncentiveFormData) => {
    const selectedBoard = getSavedBoardSelection();

    return {
      name: formData.name,
      task: formData.taskId,
      resources: formData.resourceIds,
      positive_tags: formData.positiveTagIds, // Already numbers
      negative_tags: formData.negativeTagIds,   // Already numbers
      details: formData.details.map(detail => ({
        document_id: detail.document_id || null,
        comment: detail.comment
      })),
      start_date: formData.startDate,
      end_date: formData.endDate,
      board: selectedBoard.id
    };
  };


  const handleSave = async (data: SpotIncentiveFormData) => {
    setIsSubmitting(true);

    try {
      const apiData = transformDataForAPI(data);
      const result = await createSpotIncentiveAPI(apiData);

      // Add the new spot incentive to the store using applyChangeset
      if (project && result?.data) {
        project.applyChangeset({
          [STORE_KEYS.spotIncentiveStore]: { added: [result.data] }
        });
      }

      onClose();
    } catch (error) {
      toast.error("Failed to create spot incentive", {
        description: "Please try again or contact support if the issue persists."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const MODAL_SIZE_CONFIG = {
    width: "min(90vw, 48rem)",
    height: "min(90vh, 47rem)",
    minWidth: "min(90vw, 48rem)",
    minHeight: "min(90vh, 47rem)",
    maxWidth: "min(90vw, 55rem)",
    maxHeight: "min(90vh, 50rem)"
  };

  return (
    <>
      <BaseModal
        title="Spot Incentive Adjustments"
        description="Award or deduct points for resources against tasks"
        onClose={onClose}
        size={MODAL_SIZE_CONFIG}
        editorInstance={editorInstance}
        record={record}
        headerIconCls=""
        modal={false}
        version=""
        contextMenuConfig={{
          [REACT_EDITOR_CONTEXT_MENU_KEYS.ARCHIVE]: {
            disabled: !memoizedPermissionMap[INCENTIVE_PERMISSION_KEYS.POINT_ARCHIVERS],
            hidden: record.isPhantom
          },
          [REACT_EDITOR_CONTEXT_MENU_KEYS.UNARCHIVE]: {
            disabled: !memoizedPermissionMap[INCENTIVE_PERMISSION_KEYS.POINT_ARCHIVERS],
            hidden: record.isPhantom
          },
          [REACT_EDITOR_CONTEXT_MENU_KEYS.DELETE]: {
            disabled: !memoizedPermissionMap[INCENTIVE_PERMISSION_KEYS.POINT_DELETERS],
            hidden: record.isPhantom
          }
        }}
        swivuAssistanceConfig={SWIVU_ASSIST_CONFIG.spotIncentive}
        footerRenderer={() => (
          <div className="flex justify-end pt-swivu-base">
            <Button
              variant="default"
              size="default"
              className="px-6 text-sm"
              onClick={form.handleSubmit(handleSave)}
              disabled={!form.watch("name") || !form.watch("taskId") || form.watch("resourceIds").length === 0 || isSubmitting}
            >
              {isSubmitting ? "Creating..." : "Adjust Points"}
            </Button>
          </div>
        )}
      >
        <BaseModalBody>
          <div className="space-y-swivu-base">
            {/* Incentive Name */}
            <div className="space-y-2">
              <label htmlFor="incentive-name" className="block text-sm font-bold text-foreground flex items-center gap-2">
                <Pencil className="h-4 w-4" />
                Incentive Name
                <span className="text-destructive">*</span>
              </label>
              <Controller
                control={form.control}
                name="name"
                render={({ field }) => (
                  <>
                    <Input
                      id="incentive-name"
                      autoComplete="off"
                      placeholder="Enter a descriptive name for this spot incentive..."
                      className="bg-background border-input text-sm placeholder:text-sm !text-sm"
                      {...field}
                    />
                    {formState.errors.name && (
                      <p className="text-sm text-destructive">{String(formState.errors.name.message)}</p>
                    )}
                  </>
                )}
              />
            </div>

            {/* Task Selection */}
            <div className="space-y-2">
              <label className="block text-sm font-bold text-foreground flex items-center gap-2">
                <Target className="h-4 w-4" />
                Task Selection
                <span className="text-destructive">*</span>
              </label>
              <Button
                variant="outline"
                className="w-full justify-start h-swivu-button"
                onClick={() => setShowTaskSelector(true)}
              >
                <div className="text-left">
                  <div className={`text-sm ${form.watch("taskId") ? "font-medium" : "text-muted-foreground"}`}>{getSelectedTaskName(form.watch("taskId"))}</div>
                </div>
              </Button>
              {formState.errors.taskId && (
                <p className="text-sm text-destructive">{String(formState.errors.taskId.message)}</p>
              )}
            </div>

            {/* Add Details Section */}
            <div className="space-y-2">
              <label className="block text-sm font-bold text-foreground flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Add Details
              </label>
              <SpotIncentiveDetailModal
                onAddDetail={handleAddDetail}
                onEditDetail={handleEditDetail}
                editingDetail={editingDetail || undefined}
                onCancelEdit={() => setEditingDetail(null)}
                editorInstance={editorInstance}
              >
                <Button variant="outline" className="w-full justify-between h-swivu-button">
                  <span className="text-sm text-muted-foreground">Add important details, images, and analysis…</span>
                </Button>
              </SpotIncentiveDetailModal>

              {/* Details Cards */}
              {form.watch("details").length > 0 && (
                <SpotIncentiveImagePreview
                  images={form.watch("details")
                    .filter((detail: any) => detail.image || detail.imageUrl) // eslint-disable-line @typescript-eslint/no-explicit-any
                    .map((detail: any) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
                      src: detail.image ? URL.createObjectURL(detail.image) : detail.imageUrl,
                      alt: detail.comment || 'Detail image',
                      caption: detail.comment
                    }))}
                  allowZoom={true}
                >
                  <div className="space-y-2 mt-3 max-h-[350px] overflow-y-auto pr-1">
                    {form.watch("details").map((detail: any, index: number) => { // eslint-disable-line @typescript-eslint/no-explicit-any
                      // Calculate the image index for lightbox navigation
                      const imageIndex = form.watch("details")
                        .slice(0, index + 1)
                        .filter((d: any) => d.image || d.imageUrl).length - 1; // eslint-disable-line @typescript-eslint/no-explicit-any

                      return (
                        <Card
                          key={detail.id}
                          className="p-3 bg-card animate-fade-in hover:bg-accent/50 transition-colors"
                        >
                          <div className="flex gap-3">
                            {(detail.image || detail.imageUrl) && (
                              <ImageThumbnail
                                src={detail.image ? URL.createObjectURL(detail.image) : detail.imageUrl}
                                alt="Detail"
                                index={imageIndex}
                              />
                            )}
                            <div
                              className="flex-1 min-w-0 cursor-pointer"
                              onClick={() => setEditingDetail(detail)}
                            >
                              <p className="text-sm text-foreground whitespace-pre-wrap break-words">
                                {detail.comment}
                              </p>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingDetail(detail);
                                }}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeDetail(detail.id);
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </SpotIncentiveImagePreview>
              )}
            </div>

            {/* Resource Selection */}
            <div className="space-y-2">
              <label className="block text-sm font-bold text-foreground flex items-center gap-2">
                <Users className="h-4 w-4" />
                Resource Selection
                <span className="text-destructive">*</span>
              </label>
              <Button
                variant="outline"
                className={`w-full justify-start ${
                  form.watch("resourceIds").length > 0 ? "!h-14" : ""
                } ${
                  !form.watch("taskId") ? "opacity-50 cursor-not-allowed" : ""
                }`}
                onClick={() => {
                  if (form.watch("taskId")) {
                    setShowResourceSelector(true);
                  }
                }}
                disabled={!form.watch("taskId")}
              >
                <div className="text-left flex-1">
                  {form.watch("resourceIds").length > 0 ? (
                    <div>
                      <div className="text-sm font-medium">
                        {getSelectedResourceNames(form.watch("resourceIds")).slice(0, 2).join(", ")}
                        {form.watch("resourceIds").length > 2 && ` +${form.watch("resourceIds").length - 2} more`}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {form.watch("resourceIds").length} resource{form.watch("resourceIds").length !== 1 ? 's' : ''} selected
                        {getDateRangeDisplay(form.watch("startDate"), form.watch("endDate"))}
                      </div>
                    </div>
                  ) : (
                    <span className={`text-sm ${form.watch("taskId") ? "text-muted-foreground" : "text-muted-foreground/60"}`}>
                      {form.watch("taskId") ? "Select Resources" : "Select Resources (Task required)"}
                    </span>
                  )}
                </div>
              </Button>
              {formState.errors.resourceIds && (
                <p className="text-sm text-destructive">{String(formState.errors.resourceIds.message)}</p>
              )}
            </div>

            {/* Points Assignment */}
            <div className="space-y-2">
              <label className="block text-sm font-bold text-foreground flex items-center gap-2">
                <Award className="h-4 w-4" />
                Assign Points
                <span className="text-destructive">*</span>
              </label>
              <Button
                variant="outline"
                className={`w-full justify-start ${
                  (form.watch("positiveTagIds").length > 0 || form.watch("negativeTagIds").length > 0) ? "!h-16" : ""
                } ${
                  !form.watch("taskId") || form.watch("resourceIds").length === 0 ? "opacity-50 cursor-not-allowed" : ""
                }`}
                onClick={() => {
                  if (form.watch("taskId") && form.watch("resourceIds").length > 0) {
                    setShowPointsAssignment(true);
                  }
                }}
                disabled={!form.watch("taskId") || form.watch("resourceIds").length === 0}
              >
                {(form.watch("positiveTagIds").length > 0 || form.watch("negativeTagIds").length > 0) ? (
                  <div className="text-left">
                    <div className="text-sm font-medium">
                      {getTotalPoints() > 0 ? '+' : ''}{getTotalPoints()} points assigned
                    </div>
                    <div className="text-sm text-muted-foreground mt-1 flex gap-3">
                      {form.watch("positiveTagIds").length > 0 && (
                        <span className="text-success">Positive: +{form.watch("positiveTagIds").reduce((sum: number, tagId: number) => {
                          const tag = project?.incentiveTagsStore?.getById(tagId);
                          return sum + (tag?.categoryRecord?.points || 0);
                        }, 0)}</span>
                      )}
                      {form.watch("negativeTagIds").length > 0 && (
                        <span className="text-destructive">Negative: -{form.watch("negativeTagIds").reduce((sum: number, tagId: number) => {
                          const tag = project?.incentiveTagsStore?.getById(tagId);
                          return sum + (tag?.categoryRecord?.points || 0);
                        }, 0)}</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <span className={`text-sm ${
                    form.watch("taskId") && form.watch("resourceIds").length > 0
                      ? "text-muted-foreground"
                      : "text-muted-foreground/60"
                  }`}>
                    {
                      form.watch("taskId") && form.watch("resourceIds").length > 0
                        ? "Select construction tags and assign points..."
                        : "Assign Points (Task & Resources required)"
                    }
                  </span>
                )}
              </Button>
            </div>

            {/* Points Detail List */}
            {(form.watch("positiveTagIds").length > 0 || form.watch("negativeTagIds").length > 0) && (
              <div className="space-y-2">
                {/* Positive Tags */}
                {form.watch("positiveTagIds").map((tagId: number) => {
                  const tag = project?.incentiveTagsStore?.getById(tagId);
                  const points = tag?.categoryRecord?.points || 0;
                  return (
                    <div key={`positive-${tagId}`} className="flex items-center justify-between bg-[#f4f4f4] dark:bg-[#181818] rounded-md px-3 py-2 gap-2">
                      <span className="text-sm font-medium flex-1 min-w-0">{tag?.name || `Tag ${tagId}`}</span>
                      <span className="text-sm font-medium text-success flex-shrink-0">+{points} pts</span>
                    </div>
                  );
                })}
                {/* Negative Tags */}
                {form.watch("negativeTagIds").map((tagId: number) => {
                  const tag = project?.incentiveTagsStore?.getById(tagId);
                  const points = tag?.categoryRecord?.points || 0;
                  return (
                    <div key={`negative-${tagId}`} className="flex items-center justify-between bg-[#f4f4f4] dark:bg-[#181818] rounded-md px-3 py-2 gap-2">
                      <span className="text-sm font-medium flex-1 min-w-0">{tag?.name || `Tag ${tagId}`}</span>
                      <span className="text-sm font-medium text-destructive flex-shrink-0">-{points} pts</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </BaseModalBody>
      </BaseModal>

      {/* Modals */}
        <SpotIncentiveTaskSelector
          open={showTaskSelector}
          onOpenChange={setShowTaskSelector}
          selectedTaskId={form.watch("taskId")}
          onTaskSelect={(taskId) => {
            form.setValue("taskId", taskId);
            form.setValue("resourceIds", []);
          }}
          editorInstance={editorInstance}
        />

      <SpotIncentiveResourceSelector
        open={showResourceSelector}
        onOpenChange={setShowResourceSelector}
        selectedResourceIds={form.watch("resourceIds")}
        onResourcesSelect={(data) => {
          form.setValue("resourceIds", data.resourceIds);
          form.setValue("startDate", data.startDate);
          form.setValue("endDate", data.endDate);
        }}
        selectedTaskId={form.watch("taskId")}
        editorInstance={editorInstance}
      />

      <SpotIncentivePointsAssignment
        open={showPointsAssignment}
        onOpenChange={setShowPointsAssignment}
        pointsAssignment={{
          positiveTagIds: form.watch("positiveTagIds"),
          negativeTagIds: form.watch("negativeTagIds")
        }}
        onPointsChange={(points) => {
          form.setValue("positiveTagIds", points.positiveTagIds);
          form.setValue("negativeTagIds", points.negativeTagIds);
        }}
        hasDetails={form.watch("details").length > 0}
        details={form.watch("details").map((detail: any) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
          document_id: detail.document_id,
          comment: detail.comment
        }))}
        editorInstance={editorInstance}
      />

    </>
  );
};

export default SpotIncentive;
